import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase';
import { collection, query, where, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { MemberModel, StudentQAModel } from '../../../../models';
import { Sparkles, Plus, Search, MessageSquare } from 'lucide-react';
import StudentQACard from './StudentQACard';
import StudentQADetailView from './StudentQADetailView';
import Header from '../studentFrame/header';

interface StudentQAProps {
    member: MemberModel;
    onAdd: () => void;
    isReadOnly?: boolean;
}

const StudentQA: React.FC<StudentQAProps> = ({ member, onAdd, isReadOnly = false }) => {
    const [qas, setQas] = useState<StudentQAModel[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [selectedQA, setSelectedQA] = useState<StudentQAModel | null>(null);

    useEffect(() => {
        const q = query(
            collection(db, 'studentQA'),
            where('memberId', '==', member.id)
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const list: StudentQAModel[] = [];
            snap.forEach(doc => list.push({ id: doc.id, ...doc.data() } as StudentQAModel));
            list.sort((a, b) => b.createdAt - a.createdAt);
            setQas(list);
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching QAs:", err);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [member.id]);

    const handleDelete = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (!confirm('정말로 이 질문 기록을 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, 'studentQA', id));
            if (selectedQA?.id === id) setSelectedQA(null);
        } catch (error) {
            console.error("Error deleting QA:", error);
        }
    };

    const filteredQas = qas.filter(q =>
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.aiAnswer.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (selectedQA) {
        return (
            <StudentQADetailView
                qa={selectedQA}
                onBack={() => setSelectedQA(null)}
            />
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Header Section */}
            {/* Header Section */}
            <Header
                icon={Sparkles}
                title="AI 질문답변"
                subtitle="궁금한 내용을 질문하고 AI 선생님의 답변을 확인하세요."
                onButtonClick={!isReadOnly ? onAdd : undefined}
                buttonText={!isReadOnly ? "새로운 질문하기" : undefined}
                buttonIcon={!isReadOnly ? Plus : undefined}
            />

            {/* Search Bar */}
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                    type="text"
                    placeholder="질문이나 답변 내용으로 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white border border-slate-100 rounded-xl pl-11 pr-4 py-3 text-sm font-medium shadow-sm focus:ring-2 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300"
                />
            </div>

            {/* QA List */}
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                    <p className="text-slate-400 font-bold">기록을 불러오는 중입니다...</p>
                </div>
            ) : filteredQas.length > 0 ? (
                <div className="space-y-4">
                    {filteredQas.map((qa) => (
                        <StudentQACard
                            key={qa.id}
                            qa={qa}
                            onView={() => setSelectedQA(qa)}
                            onDelete={(e) => handleDelete(e, qa.id)}
                        />
                    ))}
                </div>
            ) : (
                <div className="py-24 text-center bg-white rounded-[4rem] border border-slate-100 shadow-sm border-dashed">
                    <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 text-slate-200">
                        <MessageSquare size={40} />
                    </div>
                    <h3 className="text-xl font-black text-slate-800">질문 기록이 없습니다</h3>
                    <p className="text-sm font-bold text-slate-400 mt-2">상단의 '새로운 질문하기'를 통해 궁금한 문제를 물어보세요!</p>
                </div>
            )}
        </div>
    );
};

export default StudentQA;

